/**
 * Simplificação do FP&A (ADR 0049), PR-3a: motor da despesa cadastrada uma única vez. Funções
 * puras e exatas (bigint, nunca ponto flutuante): meses da despesa pela recorrência e pela base de
 * cálculo, reajuste simples e rateio percentual pelo maior resto. O banco confere de novo as
 * somas; as linhas do plano são a soma das parcelas.
 */
import { z } from 'zod';
import { checkAcquisition, fpaPurposeFields } from './fpa-aquisicao.ts';
import { fpaDecimal, fpaMonth, monthsOfYear } from './fpa.ts';
import { fromScaled, toScaled } from './money.ts';

export const FPA_EXPENSE_RECURRENCES = [
  'once',
  'monthly',
  'bimonthly',
  'quarterly',
  'semiannual',
  'annual',
  'custom',
] as const;
export type FpaExpenseRecurrence = (typeof FPA_EXPENSE_RECURRENCES)[number];

/** Base de cálculo: unitário × quantidade, total por ocorrência ou total do período dividido. */
export const FPA_EXPENSE_BASES = ['unit', 'occurrence', 'period'] as const;
export type FpaExpenseBasis = (typeof FPA_EXPENSE_BASES)[number];

/** Critério de rateio. Só o percentual agora; os demais ficam para os PRs seguintes. */
export const FPA_ALLOCATION_METHODS = ['percent', 'quantity'] as const;
export type FpaAllocationMethod = (typeof FPA_ALLOCATION_METHODS)[number];

const STEP: Record<Exclude<FpaExpenseRecurrence, 'custom'>, number> = {
  once: 0,
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
};

const SCALE = 4; // casas do numeric(19,4)
const pow10 = (n: number) => 10n ** BigInt(n);
/** Percentual com até 4 casas, na escala de 4 casas (100% = 1.000.000). */
const HUNDRED = 100n * pow10(SCALE);

/** Arredonda (meio para cima, longe do zero) para as casas da moeda. */
export function roundToCurrency(scaled: bigint, minorUnits: number): bigint {
  const step = pow10(SCALE - minorUnits);
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const rounded = ((abs + step / 2n) / step) * step;
  return negative ? -rounded : rounded;
}

function divRoundHalfUp(n: bigint, d: bigint): bigint {
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = (an * 2n + ad) / (2n * ad);
  return negative ? -q : q;
}

function addMonths(month: string, n: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + n;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  return `${yy}-${String(mm + 1).padStart(2, '0')}-01`;
}

/**
 * Divide um valor inteiro (na escala de 4 casas, já nas casas da moeda) por pesos, pelo maior
 * resto, sem sobra nem falta: a soma das partes é exatamente o valor. Empate no resto: vence o
 * menor `tieKey` (ex.: o menor código de centro de custo).
 */
export function largestRemainder(
  total: bigint,
  minorUnits: number,
  weights: readonly { weight: bigint; tieKey: string }[],
): bigint[] {
  if (weights.length === 0) return [];
  const step = pow10(SCALE - minorUnits);
  const negative = total < 0n;
  const units = (negative ? -total : total) / step;
  const sum = weights.reduce((s, w) => s + w.weight, 0n);
  if (sum <= 0n) throw new Error('Os pesos do rateio precisam somar mais que zero');
  const parts = weights.map((w, i) => {
    const num = units * w.weight;
    return { i, base: num / sum, rest: num % sum, tieKey: w.tieKey };
  });
  let left = units - parts.reduce((s, p) => s + p.base, 0n);
  const order = [...parts].sort((a, b) =>
    a.rest === b.rest
      ? a.tieKey < b.tieKey
        ? -1
        : a.tieKey > b.tieKey
          ? 1
          : a.i - b.i
      : a.rest > b.rest
        ? -1
        : 1,
  );
  const out = parts.map((p) => p.base);
  for (const p of order) {
    if (left <= 0n) break;
    out[p.i] = out[p.i]! + 1n;
    left -= 1n;
  }
  return out.map((u) => (negative ? -u : u) * step);
}

export interface FpaExpensePlanInput {
  recurrence: FpaExpenseRecurrence;
  basis: FpaExpenseBasis;
  unitAmount?: string | null;
  quantity?: string | null;
  amount?: string | null;
  startMonth: string;
  endMonth?: string | null;
  adjustmentPercent?: string | null;
  adjustmentFrom?: string | null;
  /** Na recorrência personalizada: o valor de cada mês, digitado. */
  customMonths?: readonly { month: string; amount: string }[];
}

export interface FpaExpenseMonthValue {
  month: string;
  /** Texto decimal (4 casas). */
  amount: string;
}

/** Os meses em que a despesa ocorre, dentro do exercício. */
export function expenseOccurrences(
  input: Pick<FpaExpensePlanInput, 'recurrence' | 'startMonth' | 'endMonth'>,
  year: number,
): string[] {
  const inYear = new Set(monthsOfYear(year));
  if (input.recurrence === 'custom') return [];
  if (input.recurrence === 'once') return inYear.has(input.startMonth) ? [input.startMonth] : [];
  const end = input.endMonth ?? `${year}-12-01`;
  const out: string[] = [];
  for (let m = input.startMonth; m <= end; m = addMonths(m, STEP[input.recurrence])) {
    if (inYear.has(m)) out.push(m);
  }
  return out;
}

/**
 * Valor de cada mês da despesa (antes do rateio), nas casas da moeda, com a fórmula usada.
 * Reajuste: um percentual simples a partir da competência informada, sem acumular.
 */
export function expenseMonths(
  input: FpaExpensePlanInput,
  year: number,
  minorUnits: number,
): { months: FpaExpenseMonthValue[]; memory: Record<string, unknown> } {
  if (input.recurrence === 'custom') {
    const inYear = new Set(monthsOfYear(year));
    const months = (input.customMonths ?? [])
      .filter((m) => inYear.has(m.month))
      .map((m) => ({
        month: m.month,
        amount: fromScaled(roundToCurrency(toScaled(m.amount), minorUnits)),
      }))
      .filter((m) => toScaled(m.amount) !== 0n)
      .sort((a, b) => (a.month < b.month ? -1 : 1));
    return { months, memory: { recurrence: 'custom', months: months.length } };
  }
  const occurrences = expenseOccurrences(input, year);
  let per: bigint[];
  let formula: string;
  if (input.basis === 'unit') {
    const unit = toScaled(input.unitAmount ?? '0');
    const qty = toScaled(input.quantity ?? '0');
    const each = roundToCurrency(divRoundHalfUp(unit * qty, pow10(SCALE)), minorUnits);
    per = occurrences.map(() => each);
    formula = `${input.quantity} × ${input.unitAmount} = ${fromScaled(each)} por ocorrência`;
  } else if (input.basis === 'occurrence') {
    const each = roundToCurrency(toScaled(input.amount ?? '0'), minorUnits);
    per = occurrences.map(() => each);
    formula = `${fromScaled(each)} por ocorrência`;
  } else {
    const total = roundToCurrency(toScaled(input.amount ?? '0'), minorUnits);
    // Ocorrências com o mesmo peso; o centavo que sobra vai para as primeiras.
    per = largestRemainder(
      total,
      minorUnits,
      occurrences.map((m) => ({ weight: 1n, tieKey: m })),
    );
    formula = `${fromScaled(total)} ÷ ${occurrences.length} ocorrências`;
  }
  const pct = input.adjustmentPercent ? toScaled(input.adjustmentPercent) : null;
  const from = input.adjustmentFrom ?? null;
  const months = occurrences.map((month, i) => {
    let v = per[i]!;
    if (pct !== null && from && month >= from) {
      v = roundToCurrency(divRoundHalfUp(v * (HUNDRED + pct), HUNDRED), minorUnits);
    }
    return { month, amount: fromScaled(v) };
  });
  return {
    months: months.filter((m) => toScaled(m.amount) !== 0n),
    memory: {
      recurrence: input.recurrence,
      basis: input.basis,
      occurrences: occurrences.length,
      formula,
      ...(pct !== null ? { adjustment: { percent: input.adjustmentPercent, from } } : {}),
    },
  };
}

export interface FpaAllocationTarget {
  /** Identificador do destino (para devolver as parcelas). */
  key: string;
  /** Percentual com até 4 casas. */
  percent: string;
  /** Código do centro de custo (desempate do maior resto). */
  costCenterCode: string;
}

/** Soma dos percentuais na escala de 4 casas (100% = 1.000.000). */
export function percentTotal(targets: readonly { percent: string }[]): bigint {
  return targets.reduce((s, t) => s + toScaled(t.percent), 0n);
}

/**
 * Parcelas de um mês: o valor do mês dividido pelos percentuais, pelo maior resto, desempate pelo
 * menor código de centro de custo. A soma é sempre o valor do mês.
 */
export interface FpaWeightedTarget {
  key: string;
  costCenterCode: string;
  /** Valor fixo do destino no mês (na moeda da despesa): recebe primeiro, sem peso. */
  fixed?: string | null;
  /** Peso do destino: percentual ou quantidade (o percentual é só exibido no rateio por quantidade). */
  weight?: string | null;
}

/**
 * Parcelas de um mês com valor fixo e pesos (ADR 0049, PR-4; regra R8): os destinos com valor fixo
 * recebem primeiro o valor deles; se os fixos passarem do valor do mês, a despesa fica com erro. O
 * restante é dividido pelos pesos (percentual ou quantidade) pelo maior resto, desempate pelo
 * menor código de centro de custo. A soma é sempre o valor do mês.
 */
export function allocateMonthWeighted(
  amount: string,
  minorUnits: number,
  targets: readonly FpaWeightedTarget[],
): { key: string; amount: string }[] {
  const total = toScaled(amount);
  const fixed = targets.filter((t) => t.fixed);
  const fixedSum = fixed.reduce((s, t) => s + toScaled(t.fixed!), 0n);
  const sameSign = fixed.every(
    (t) => toScaled(t.fixed!) === 0n || toScaled(t.fixed!) < 0n === total < 0n,
  );
  if (!sameSign || (fixedSum < 0n ? -fixedSum : fixedSum) > (total < 0n ? -total : total)) {
    throw new Error('Os valores fixos passam do valor do mês');
  }
  const rest = total - fixedSum;
  const weighted = targets.filter((t) => !t.fixed);
  if (weighted.length === 0 && rest !== 0n) {
    throw new Error('Os valores fixos não somam o valor do mês e não há destino com peso');
  }
  const parts = weighted.length
    ? largestRemainder(
        rest,
        minorUnits,
        weighted.map((t) => ({ weight: toScaled(t.weight ?? '0'), tieKey: t.costCenterCode })),
      )
    : [];
  const byKey = new Map(weighted.map((t, i) => [t.key, parts[i]!]));
  return targets.map((t) => ({
    key: t.key,
    amount: fromScaled(t.fixed ? toScaled(t.fixed) : byKey.get(t.key)!),
  }));
}

/** Quantidade do destino no mês: a última mudança "a partir de" até o mês, ou a quantidade base. */
export function quantityInMonth(
  base: string | null | undefined,
  changes: readonly { from: string; quantity: string }[],
  month: string,
): string | null {
  const m = month.slice(0, 7);
  const last = [...changes]
    .filter((c) => c.from.slice(0, 7) <= m)
    .sort((a, b) => a.from.localeCompare(b.from))
    .pop();
  return last?.quantity ?? base ?? null;
}

export function allocateMonth(
  amount: string,
  minorUnits: number,
  targets: readonly FpaAllocationTarget[],
): { key: string; amount: string }[] {
  if (percentTotal(targets) !== HUNDRED) {
    throw new Error('Os percentuais do rateio precisam somar 100%');
  }
  const parts = largestRemainder(
    toScaled(amount),
    minorUnits,
    targets.map((t) => ({ weight: toScaled(t.percent), tieKey: t.costCenterCode })),
  );
  return targets.map((t, i) => ({ key: t.key, amount: fromScaled(parts[i]!) }));
}

// ---------------------------------------------------------------------------
// Entrada da API
// ---------------------------------------------------------------------------

const uuid = z.string().uuid();
const percent = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Use um percentual com até 4 casas')
  .refine((v) => toScaled(v) > 0n && toScaled(v) <= HUNDRED, 'Use um percentual entre 0 e 100');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

const positive = fpaDecimal.refine((v) => toScaled(v) > 0n, 'Use um valor maior que zero');

export const fpaExpenseTargetInput = z.object({
  legalEntityId: uuid,
  costCenterId: uuid,
  /** Peso no rateio por percentual (vazio no destino com valor fixo ou no rateio por quantidade). */
  percent: percent.nullish().transform((v) => v ?? null),
  /** Peso no rateio por quantidade (ex.: 20 licenças), com as mudanças "a partir de" um mês. */
  quantity: positive.nullish().transform((v) => v ?? null),
  quantityChanges: z
    .array(z.object({ from: fpaMonth, quantity: positive }))
    .max(12)
    .default([]),
  /** Valor fixo por mês, na moeda da despesa: o destino recebe primeiro (PR-4). */
  fixedAmount: fpaDecimal
    .refine((v) => toScaled(v) !== 0n, 'Use um valor diferente de zero')
    .nullish()
    .transform((v) => v ?? null),
  expenseTypeId: uuid,
  classificationId: uuid,
  /** Justificativa quando a classificação foge da sugestão do item (vira exceção para o FP&A). */
  exceptionNote: optionalText(1000),
});
export type FpaExpenseTargetInput = z.infer<typeof fpaExpenseTargetInput>;

/** Data da última alteração que a pessoa viu (edição simultânea: se mudou, a gravação é recusada). */
const expectedUpdatedAt = z
  .string()
  .datetime({ offset: true })
  .nullish()
  .transform((v) => v ?? null);

const fpaExpenseFields = z.object({
  name: z.string().trim().min(1).max(200),
  // Finalidade do frete e gastos de aquisição (ADR 0032, 13).
  ...fpaPurposeFields,
  /** Atalho do Catálogo (opcional, PR-8e): a classificação oficial vem de cada destino. */
  spendItemId: uuid.nullish().transform((v) => v ?? null),
  partnerId: uuid.nullish().transform((v) => v ?? null),
  supplierName: optionalText(200),
  contractingEntityId: uuid,
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/),
  basis: z.enum(FPA_EXPENSE_BASES),
  unitAmount: fpaDecimal.nullish().transform((v) => v ?? null),
  quantity: fpaDecimal.nullish().transform((v) => v ?? null),
  amount: fpaDecimal.nullish().transform((v) => v ?? null),
  recurrence: z.enum(FPA_EXPENSE_RECURRENCES),
  startMonth: fpaMonth,
  endMonth: fpaMonth.nullish().transform((v) => v ?? null),
  adjustmentPercent: fpaDecimal.nullish().transform((v) => v ?? null),
  adjustmentFrom: fpaMonth.nullish().transform((v) => v ?? null),
  customMonths: z
    .array(z.object({ month: fpaMonth, amount: fpaDecimal }))
    .max(12)
    .default([]),
  justification: optionalText(1000),
  allocationMethod: z.enum(FPA_ALLOCATION_METHODS).default('percent'),
  /** Quantidade digitada ou contada nos colaboradores (PR-5), com o mês da contagem. */
  quantitySource: z.enum(['manual', 'employees']).default('manual'),
  quantityCountedOn: fpaMonth.nullish().transform((v) => v ?? null),
  targets: z.array(fpaExpenseTargetInput).min(1).max(200),
  expectedUpdatedAt,
});

export const fpaExpenseInput = fpaExpenseFields.superRefine((v, ctx) => {
  const issue = (path: string, message: string) =>
    ctx.addIssue({ code: 'custom', path: [path], message });
  checkAcquisition(v, issue);
  if (v.recurrence === 'custom') {
    if (v.customMonths.length === 0) issue('customMonths', 'Informe o valor de pelo menos um mês');
    if (new Set(v.customMonths.map((m) => m.month)).size !== v.customMonths.length) {
      issue('customMonths', 'Cada mês aparece uma vez só');
    }
    if (v.adjustmentPercent)
      issue('adjustmentPercent', 'Na personalizada, digite os meses já reajustados');
  } else if (v.basis === 'unit') {
    if (!v.unitAmount) issue('unitAmount', 'Informe o valor unitário');
    if (!v.quantity) issue('quantity', 'Informe a quantidade');
  } else if (!v.amount) {
    issue('amount', 'Informe o valor');
  }
  if (v.endMonth && v.endMonth < v.startMonth) {
    issue('endMonth', 'A competência final vem depois da inicial');
  }
  if (!!v.adjustmentPercent !== !!v.adjustmentFrom) {
    issue('adjustmentFrom', 'Reajuste precisa do percentual e da competência');
  }
  // Rateio (PR-4): destino com valor fixo não tem peso; os demais têm o peso do critério.
  const weighted = v.targets.filter((t) => !t.fixedAmount);
  for (const [i, t] of v.targets.entries()) {
    if (t.fixedAmount) continue;
    if (v.allocationMethod === 'percent' && !t.percent) {
      ctx.addIssue({
        code: 'custom',
        path: ['targets', i, 'percent'],
        message: 'Informe o percentual',
      });
    }
    if (v.allocationMethod === 'quantity' && !t.quantity) {
      ctx.addIssue({
        code: 'custom',
        path: ['targets', i, 'quantity'],
        message: 'Informe a quantidade',
      });
    }
  }
  if (
    v.allocationMethod === 'percent' &&
    weighted.length > 0 &&
    weighted.every((t) => t.percent) &&
    percentTotal(weighted.map((t) => ({ percent: t.percent! }))) !== HUNDRED
  ) {
    issue('targets', 'Os percentuais do rateio precisam somar 100%');
  }
  const keys = v.targets.map((t) => `${t.costCenterId}|${t.expenseTypeId}|${t.classificationId}`);
  if (new Set(keys).size !== keys.length) {
    issue('targets', 'O mesmo centro e a mesma classificação aparecem duas vezes');
  }
});
export type FpaExpenseInput = z.infer<typeof fpaExpenseInput>;

/**
 * Rascunho ("Em preenchimento", B1): o formulário como a pessoa deixou. Só o nome é obrigatório;
 * o resto pode estar incompleto, mas o que vier preenchido tem o formato certo. Não entra em
 * nenhum total: ao concluir, vale a entrada completa (fpaExpenseInput).
 */
export const fpaExpenseDraftInput = z.object({
  name: z.string().trim().min(1).max(200),
  draft: fpaExpenseFields
    .omit({ name: true, targets: true, expectedUpdatedAt: true })
    .partial()
    .extend({
      // Par oficial escolhido no formulário, guardado mesmo antes de haver destino (PR-8e).
      expenseTypeId: uuid.optional(),
      classificationId: uuid.optional(),
      exceptionNote: z.string().trim().max(1000).optional(),
      targets: z
        .array(
          fpaExpenseTargetInput.partial().extend({
            percent: z.string().trim().max(20).nullish(),
          }),
        )
        .max(200)
        .default([]),
    })
    .default({ targets: [] }),
  expectedUpdatedAt,
});
export type FpaExpenseDraftInput = z.infer<typeof fpaExpenseDraftInput>;

/** Filtros da lista de despesas da versão. */
export const fpaExpenseListQuery = z.object({
  legalEntityId: uuid.optional(),
  costCenterId: uuid.optional(),
  status: z.enum(['draft', 'active', 'cancelled']).optional(),
  pending: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});

/** Uma linha da grade (célula sem o mês), para consultar as despesas de origem. */
export const fpaExpenseSourcesQuery = z.object({
  legalEntityId: uuid,
  costCenterId: uuid,
  expenseTypeId: uuid,
  classificationId: uuid,
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/),
});

export const fpaSupplierSearchQuery = z.object({
  q: z.string().trim().min(2).max(100),
  legalEntityId: uuid,
});

// ---------------------------------------------------------------------------
// Conferência da versão e exceções de classificação (ADR 0049, PR-3c)
// ---------------------------------------------------------------------------

/**
 * Pendências da conferência, na ordem em que aparecem. `block` impede a publicação; `warn` é
 * aviso, confirmado na hora de publicar. `fixBy` diz quem corrige e `where`, onde: na despesa,
 * na fila de exceções do FP&A ou na grade da versão.
 */
export const FPA_VERSION_CHECKS = {
  classification_pending: { fixBy: 'fpa', where: 'queue' },
  classification_returned: { fixBy: 'author', where: 'expense' },
  classification_invalid: { fixBy: 'author', where: 'expense' },
  line_classification_invalid: { fixBy: 'planner', where: 'grid' },
  center_invalid: { fixBy: 'author', where: 'expense' },
  line_center_invalid: { fixBy: 'planner', where: 'grid' },
  reconciliation: { fixBy: 'support', where: null },
  draft_expense: { fixBy: 'author', where: 'expense' },
  item_inactive: { fixBy: 'author', where: 'expense' },
  item_revised: { fixBy: 'author', where: 'expense' },
  center_inactive: { fixBy: 'author', where: 'expense' },
  possible_duplicate: { fixBy: 'planner', where: 'grid' },
  missing_rate: { fixBy: 'fpa', where: 'rates' },
} as const satisfies Record<
  string,
  {
    fixBy: 'fpa' | 'author' | 'planner' | 'support';
    where: 'queue' | 'expense' | 'grid' | 'rates' | null;
  }
>;
export type FpaVersionCheckCode = keyof typeof FPA_VERSION_CHECKS;
export type FpaCheckSeverity = 'block' | 'warn';

/** Aceitar ou devolver uma exceção de classificação; devolver exige o motivo. */
export const fpaClassificationDecisionInput = z
  .object({
    decision: z.enum(['accept', 'return']),
    note: z.string().trim().max(1000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'return' && (v.note ?? '').length < 3) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'reason_required' });
    }
    if (v.note && v.note.length < 3) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'too_short' });
    }
  });
export type FpaClassificationDecisionInput = z.infer<typeof fpaClassificationDecisionInput>;

/** Colaboradores por centro (consulta agregada, PR-5): a empresa e o mês da contagem. */
export const fpaHeadcountQuery = z.object({ legalEntityId: uuid, month: fpaMonth });

/** Fila de exceções: de uma versão ou de todas as versões em rascunho. */
export const fpaClassificationQueueQuery = z.object({
  versionId: uuid.optional(),
  status: z.enum(['exception', 'returned']).optional(),
});
export type FpaClassificationQueueQuery = z.infer<typeof fpaClassificationQueueQuery>;

/** Publicar: com avisos, só depois de a pessoa confirmar que viu (a conferência é no servidor). */
export const fpaPublishInput = z.object({ acknowledgeWarnings: z.boolean().default(false) });
/** Envio da versão para aprovação: a empresa e o centro de custo responsáveis escolhem a regra. */
export const fpaSubmitInput = fpaPublishInput.extend({
  legalEntityId: z.uuid(),
  costCenterId: z.uuid(),
});
